<template>
  <v-card class="secondary-market-orders-table">
    <v-card-title class="toolbar white--text">
      <h2>Secondary Market Orders ({{ filteredItems.length }} of {{ pagination.totalItems }})</h2>
      <v-spacer />
      <v-text-field
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        label="Search orders by userId, snr, tradeId..."
        single-line
        hide-details
      />
    </v-card-title>

    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filtered results ({{ filteredResults.length }} of {{ filteredItems.length }})
        <v-tooltip
          v-if="!hasResultFilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetResultsFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-text-field
        v-model="resultFilters.searchResults"

        class="ml-5"
        append-icon="search"
        label="filter results"
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredResults"
      :total-items.sync="secondaryMarketOrdersPagination.total"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      :expand="expand"
      item-key="id"
      class="elevation-1"
      :pagination.sync="pagination"
      @update:pagination="updatePagination"
    >
      <template v-slot:items="props">
        <tr
          :key="`${props.index}${props.item.trade_id}`"
          :active="props.selected"
          @click="props.expanded = !props.expanded"
        >
          <td class="text-xs-center">
            {{ props.item.trade_id }}
          </td>
          <td class="text-xs-center">
            <span v-if="props.item.buyer">
              {{ props.item.buyer.user_id }}
            </span>
            <v-tooltip
              v-else
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="error"
                  flat
                  icon
                  v-on="on"
                >
                  <v-icon>error</v-icon>
                </v-btn>
              </template>
              <span>Error getting buyer information</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <span v-if="props.item.seller">
              {{ props.item.seller.user_id }}
            </span>
            <v-tooltip
              v-else
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="error"
                  flat
                  icon
                  v-on="on"
                >
                  <v-icon>error</v-icon>
                </v-btn>
              </template>
              <span>Error getting seller information</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            {{ props.item.nominal_value }} €
          </td>
          <td class="text-xs-center">
            {{ props.item.sale_price }}
          </td>
          <td class="text-xs-center">
            {{ props.item.wpnr }}
          </td>
          <td class="text-xs-center">
            {{ formatDateSmall(props.item.order.upload_date) }}
          </td>
          <td class="text-xs-center">
            {{ props.item.success }}
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.created_at) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.created_at) }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.updated_at) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.updated_at) }}
            </v-tooltip>
          </td>
        </tr>
      </template>
      <template v-slot:expand="props">
        <BaaderTradeRowDetail
          :item="props.item"
          :buyer-snr="props.item.buyer ? props.item.buyer.snr : 0"
          :seller-snr="props.item.seller ? props.item.seller.snr : 0"
        />
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions } from 'vuex';
import { toQueryString, setQuery } from '@/helpers';
import PayoutStatusEnum from '@/enum/baader/PayoutStatus';
import BaaderTradeRowDetail from './shared/BaaderTradeRowDetail';

export default {
  name: 'SecondaryMarketOrdersTable',
  components: {
    BaaderTradeRowDetail,
  },
  props: {
    secondaryMarketOrders: {
      type: Array,
      default: () => [],
    },
    secondaryMarketOrdersPagination: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
      isLoadTableData: false,
      search: undefined,
      selected: [],
      pagination: {
        page: this.secondaryMarketOrdersPagination.page,
        rowsPerPage: this.secondaryMarketOrdersPagination.limit,
        sortBy: 'created_at',
        descending: true,
      },
      rowsPerPageItems: [
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ],
      filters: {
        createdAt: [],
        state: [],
      },
      resultFilters: {
        searchResults: null,
      },
      headers: [
        {
          text: 'Trade Id',
          align: 'left',
          value: 'trade_id',
        },
        {
          text: 'Buyer',
          align: 'left',
          value: 'buyer.user_id',
        },
        {
          text: 'Seller',
          align: 'left',
          value: 'seller.user_id',
        },
        {
          text: 'NominalValue',
          align: 'left',
          value: 'nominal_value',
        },
        {
          text: 'SalePrice',
          align: 'left',
          value: 'sale_price',
        },
        {
          text: 'WPNR',
          align: 'left',
          value: 'wpnr',
        },
        {
          text: 'Upload Date',
          align: 'left',
          value: 'order.upload_date',
        },
        {
          text: 'Success',
          align: 'left',
          value: 'success',
        },
        {
          text: 'Created at',
          value: 'created_at',
        },
        {
          text: 'Updated at',
          value: 'updated_at',
        },
      ],
    };
  },
  computed: {
    filteredItems() {
      return this.secondaryMarketOrders.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          return (
            this.filters[f].length < 1 ||
            String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
    filteredResults() {
      if (this.hasResultFilters) {
        return this.filteredItems.filter((item) => {
          let hasStatus = false;
          let hasSearch = false;

          const { orderStatus, searchResults } = this.resultFilters;

          if (orderStatus) {
            hasStatus = item.order.status === orderStatus;
          }

          if (searchResults) {
            hasSearch = JSON.stringify(item)
              .toLowerCase()
              .includes(searchResults.toLowerCase());
          }

          return (!!hasStatus || !!hasSearch) || false;
        });
      }

      return this.filteredItems;
    },

    orderStatus() {
      return Object.keys(PayoutStatusEnum).map(key => ({
        text: this.formatFieldName(PayoutStatusEnum[key]),
        value: PayoutStatusEnum[key],
      }));
    },
    hasResultFilters() {
      return !!this.resultFilters.orderStatus
        || !!this.resultFilters.searchResults;
    },
  },
  watch: {
    async secondaryMarketOrders() {
      this.getDefaultFilters();
      await this.$nextTick();
      await this.changePage();
    },
    secondaryMarketOrdersPagination: {
      immediate: false,
      async handler(secondaryMarketOrdersPagination) {
        if (secondaryMarketOrdersPagination) {
          this.pagination.page = secondaryMarketOrdersPagination.page;
          this.pagination.rowsPerPage = secondaryMarketOrdersPagination.limit;
          this.pagination.totalItems = secondaryMarketOrdersPagination.total;
        }
      },
    },
    search: {
      immediate: false,
      handler(search, oldSearch) {
        if (search !== oldSearch) {
          setQuery(
            {
              search,
            },
            this.$route,
            this.$router,
          );
          this.refreshTable();
        }
      },
    },
    pagination: {
      immediate: false,
      handler(pagination, oldPagination) {
        if (!_.isEqual({
          descending: pagination.descending,
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          sortBy: pagination.sortBy,
          totalItems: pagination.totalItems,
        }, {
          descending: oldPagination.descending,
          page: oldPagination.page,
          rowsPerPage: oldPagination.rowsPerPage,
          sortBy: oldPagination.sortBy,
          totalItems: oldPagination.totalItems || 0,
        })) {
          if (pagination.sortBy) {
            this.refreshTable();
          }
        }
      },
    },
  },
  methods: {
    ...mapActions(['getSecondaryMarketOrders']),
    refreshTable() {
      const query = toQueryString({
        page: this.pagination.page || 1,
        limit: this.pagination.rowsPerPage || 25,
        search: this.search,
        sort: this.pagination.sortBy,
        dir: this.pagination.descending ? 'desc' : 'asc',
      });
      if (typeof window.LIT !== 'undefined') {
        clearTimeout(window.LIT);
      }
      window.LIT = setTimeout(() => {
        this.getSecondaryMarketOrders(query);
      }, 1000);
    },
    formatDate(date) {
      return this.$moment(date).format('MMMM Do YYYY, h:mm:ss a');
    },
    formatDateSmall(date) {
      if (date) {
        return this.$moment(date).format('DD/MM/YYYY');
      }

      return '-';
    },

    /**
     * removes keys from object if value is falsy or typeof object
     */
    cleanFields(data) {
      return Object.keys(data)
        .filter(field => !!data[field] && typeof data[field] !== 'object');
    },
    updatePagination(pagination) {
      setQuery(
        {
          page: pagination.page,
          sortBy: pagination.sortBy,
          dir: pagination.descending ? 'desc' : 'asc',
          search: this.search,
        },
        this.$route,
        this.$router,
      );
    },
    toogleSelected(props) {
      props.selected = !props.selected;
    },
    async changePage() {
      const { page, search } = this.$route.query;
      this.search = search;
      await this.$nextTick();
      this.isLoadTableData = true;
      this.pagination.page = page ? Number(page) : 1;
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    formatFieldName(str, item) {
      const camelCaseSentence = str.replace(/([-_]\w)/g, g => g[1].toUpperCase());
      const sentence = _.startCase(camelCaseSentence);

      if (item) {
        if (str === 'security_wallet' && item.sell_offer.asset.provider === 'baaderbank') {
          return `${_.capitalize(sentence)} / Baader Bank account`;
        }
      }

      return _.capitalize(sentence);
    },

    resetResultsFilters() {
      this.resultFilters = _.mapValues(this.resultFilters, () => null);
    },

    checkObjectKeys(item, value) {
      return JSON.stringify(item)
        .toLowerCase()
        .includes(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.secondary-market-orders-table{
  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }

  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-select{
        flex: 1;
  }

  table.v-table {
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .v-input{
    &.security_wallet{
      min-width: 350px;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/baader/SecondaryMarketOrdersTable.vue